import { useEffect, useState, useRef } from "react" // 💡 1. useRef 추가
import { useNavigate, useLocation } from "react-router-dom"
import Header from "@/components/layout/Header"
import Footer from "@/components/layout/Footer"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Separator } from "@/components/ui/separator"
import { Checkbox } from "@/components/ui/checkbox"
import { useAuth } from "@/contexts/AuthContext"
import { getCart } from "@/api/cartApi"
import { createOrder } from "@/api/orderApi"
import { getAddresses } from "@/api/addressApi"

const formatPrice = (value: number) => `${value.toLocaleString("ko-KR")}원`

// 고객용 자주 쓰는 경조사어 프리셋
const QUICK_PHRASES = [
    "축발전(祝發展)", "축개업(祝開業)", "축화혼(祝華婚)", "삼가 故人의 冥福을 빕니다", "근조(謹弔)",
    "축결혼(祝結婚)", "축승진(祝昇進)", "대박나세요", "무궁한 발전을 기원합니다"
]

export default function OrderPage() {
    const navigate = useNavigate()
    const location = useLocation()
    const { isLoggedIn, isLoading, user } = useAuth()

    const isGuest = location.state?.isGuest || false
    const isDirectBuy = location.state?.directBuyItems ? true : false

    const [cartItems, setCartItems] = useState<any[]>([])
    const [addresses, setAddresses] = useState<any[]>([])
    const [pageLoading, setPageLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [isSameAsOrderer, setIsSameAsOrderer] = useState(false)
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false)

    // 비회원 주문 완료 시 발급된 주문번호를 담을 상태
    const [successOrderNo, setSuccessOrderNo] = useState<string | null>(null)

    // 클립보드 복사 함수
    const handleCopyOrderNo = async () => {
        if (!successOrderNo) return;
        try {
            await navigator.clipboard.writeText(successOrderNo);
            alert("주문번호가 복사되었습니다.");
        } catch (err) {
            alert("복사에 실패했습니다. 화면의 주문번호를 직접 선택하여 복사해주세요.");
        }
    };

    // 💡 2. 이메일 입력창을 참조할 useRef 생성
    const emailInputRef = useRef<HTMLInputElement>(null)

    const [form, setForm] = useState({
        ordererName: "", ordererPhone: "", ordererEmail: "", receiverName: "", receiverPhone: "",
        zipcode: "", address1: "", address2: "", deliveryMemo: "", paymentMethod: "card",
        orderPassword: ""
    })

    const [isPostcodeOpen, setIsPostcodeOpen] = useState(false)

    // ==========================================
    // 배송날짜 제한 (오늘 ~ 90일) 로직 추가
    // ==========================================
    const { minDate, maxDate } = (() => {
        const today = new Date()
        const kstOffset = 9 * 60 * 60 * 1000
        const kstToday = new Date(today.getTime() + kstOffset)

        // 최소 날짜: 오늘
        const min = kstToday.toISOString().split('T')[0]

        // 최대 날짜: 오늘로부터 + 90일
        const kstMax = new Date(kstToday.getTime() + (90 * 24 * 60 * 60 * 1000))
        const max = kstMax.toISOString().split('T')[0]

        return { minDate: min, maxDate: max }
    })()

    // ==========================================
    // 배송/행사 일정 상태
    // ==========================================
    // const [deliveryDate, setDeliveryDate] = useState(() => {
    //     const today = new Date()
    //     const kstOffset = 9 * 60 * 60 * 1000
    //     return new Date(today.getTime() + kstOffset).toISOString().split('T')[0]
    // })
    const [deliveryDate, setDeliveryDate] = useState(minDate) // 💡 기존의 복잡한 초기화 코드를 minDate로 깔끔하게 교체!

    const [deliveryDay, setDeliveryDay] = useState("")
    const [deliveryType, setDeliveryType] = useState<"immediate" | "specified">("immediate")
    const [deliveryHour, setDeliveryHour] = useState("09")
    const [deliveryMinute, setDeliveryMinute] = useState("00")
    const [eventTime, setEventTime] = useState("")

    useEffect(() => {
        if (!deliveryDate) { setDeliveryDay(""); return; }
        const days = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일']
        const dateObj = new Date(deliveryDate)
        if (!isNaN(dateObj.getTime())) setDeliveryDay(days[dateObj.getDay()])
    }, [deliveryDate])

    // ==========================================
    // 메시지 (리본/카드) 상태
    // ==========================================
    const [messageType, setMessageType] = useState<"none" | "ribbon" | "card" | "ribbon_card">("none")
    const [ribbonPhrase, setRibbonPhrase] = useState("")
    const [ribbonSender, setRibbonSender] = useState("")
    const [cardContent, setCardContent] = useState("")

    useEffect(() => {
        if (isPostcodeOpen) {
            // 1. Daum 우편번호 객체 실행 함수
            const initDaumPostcode = () => {
                const daum = (window as any).daum
                new daum.Postcode({
                    oncomplete: function(data: any) {
                        setForm(prev => ({
                            ...prev,
                            zipcode: data.zonecode,
                            address1: data.address,
                            address2: ""
                        }))
                        setIsPostcodeOpen(false)
                    },
                    width: '100%',
                    height: '100%'
                }).embed(document.getElementById('daum-postcode-container'))
            }

            // 2. 이미 스크립트가 로드되어 있는지 확인
            if ((window as any).daum && (window as any).daum.Postcode) {
                initDaumPostcode() // 이미 있으면 바로 실행
            } else {
                // 3. 스크립트가 없다면 동적으로 HTML 문서 <head>에 추가
                const script = document.createElement("script")
                script.src = "//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js"
                script.async = true
                script.onload = () => initDaumPostcode() // 스크립트 로드 완료 후 실행

                document.head.appendChild(script)
            }
        }
    }, [isPostcodeOpen])

    useEffect(() => {
        if (isLoading) return

        if (!isLoggedIn && !isGuest) {
            alert("로그인이 필요합니다.")
            navigate("/login")
            return
        }

        const fetchData = async () => {
            try {
                const directBuyItems = location.state?.directBuyItems
                if (directBuyItems && directBuyItems.length > 0) {
                    setCartItems(directBuyItems)
                } else if (!isGuest) {
                    const cartRes = await getCart()
                    setCartItems(cartRes.data || [])
                }

                if (!isGuest) {
                    const addrRes = await getAddresses()
                    const addrList = addrRes.data || []
                    setAddresses(addrList)

                    const defaultAddr = addrList.find((a: any) => a.defaultYn === 'Y')

                    setForm(prev => ({
                        ...prev,
                        ordererName: user?.memberName || "",
                        ordererPhone: user?.phone || "",
                        ordererEmail: user?.email || "",
                        receiverName: defaultAddr ? defaultAddr.receiverName : (user?.memberName || ""),
                        receiverPhone: defaultAddr ? defaultAddr.receiverPhone : "",
                        zipcode: defaultAddr ? defaultAddr.zipcode : "",
                        address1: defaultAddr ? defaultAddr.address1 : "",
                        address2: defaultAddr ? defaultAddr.address2 : "",
                    }))
                }
            } catch (error) {
                console.error("데이터 로드 실패:", error)
            } finally {
                setPageLoading(false)
            }
        }
        fetchData()
    }, [isLoggedIn, isLoading, navigate, location.state, user, isGuest])

    const handleSameAsOrderer = (checked: boolean) => {
        setIsSameAsOrderer(checked)
        if (checked) {
            setForm(prev => ({
                ...prev,
                receiverName: prev.ordererName,
                receiverPhone: prev.ordererPhone
            }))
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { id, value } = e.target
        setForm(prev => ({ ...prev, [id]: value }))
        if (isSameAsOrderer && (id === "receiverName" || id === "receiverPhone")) {
            setIsSameAsOrderer(false)
        }
    }

    const handleSelectAddress = (addr: any) => {
        setForm(prev => ({
            ...prev,
            receiverName: addr.receiverName,
            receiverPhone: addr.receiverPhone,
            zipcode: addr.zipcode,
            address1: addr.address1,
            address2: addr.address2,
        }))
        setIsSameAsOrderer(false)
        setIsAddressModalOpen(false)
    }

    const productTotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0)
    const shippingFee = productTotal >= 50000 ? 0 : 3000
    const discountAmount = isGuest ? 0 : 0
    const finalTotal = productTotal + shippingFee - discountAmount

    // ==========================================
    // 결제 실행 로직 (유효성 검사 및 포커스 추가)
    // ==========================================
    const handleSubmitOrder = async () => {
        if (!form.ordererName || !form.ordererPhone) return alert("주문자 정보를 입력해주세요.")

        // 💡 3. 이메일 검증 실패 시 alert 후 이메일 입력창으로 자동 포커스
        if (!form.ordererEmail || !form.ordererEmail.trim()) {
            alert("결제 내역을 안내받으실 이메일을 입력해주세요.")
            emailInputRef.current?.focus()
            return
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(form.ordererEmail)) {
            alert("올바른 이메일 형식이 아닙니다. 다시 확인해주세요.")
            emailInputRef.current?.focus()
            return
        }

        if (!form.receiverName || !form.receiverPhone || !form.address1) return alert("배송 정보를 입력해주세요.")

        if (isGuest && !form.orderPassword) {
            return alert("비회원 주문 조회를 위한 비밀번호를 입력해주세요.")
        }

        setSubmitting(true)

        // 서버에 보낼 주문 통합 Payload 구성
        const orderPayload = {
            ...form,
            paymentMethod: form.paymentMethod.toUpperCase(),
            items: cartItems,
            totalAmount: finalTotal,
            isGuest: isGuest,
            isDirectBuy: isDirectBuy,
            deliveryDate,
            deliveryType,
            deliveryTime: deliveryType === "specified" ? `${deliveryHour}:${deliveryMinute}` : null,
            eventTime,
            messageType,
            ribbonPhrase: ["ribbon", "ribbon_card"].includes(messageType) ? ribbonPhrase : "",
            ribbonSender: ["ribbon", "ribbon_card"].includes(messageType) ? ribbonSender : "",
            cardContent: ["card", "ribbon_card"].includes(messageType) ? cardContent : ""
        }

        try {
            // 후결제(무통장입금/현장결제)인 경우 포트원 생략하고 바로 백엔드 전송
            if (form.paymentMethod === "post_pay") {

                const tempOrderNo = `POST_PAY_${new Date().getTime()}`;
                const res = await createOrder({
                    ...orderPayload,
                    impUid: tempOrderNo,
                    merchantUid: tempOrderNo
                })

                // 💡 백엔드 응답에서 주문번호 추출 (없으면 생성한 tempOrderNo 사용)
                // const finalOrderNo = res?.data?.orderNo || res?.orderNo || tempOrderNo;
                const finalOrderNo = (res && res.data) ? res.data : tempOrderNo;

                if (isGuest) {
                    setSuccessOrderNo(finalOrderNo); // 비회원은 모달 오픈
                } else {
                    alert("주문이 성공적으로 접수되었습니다. (후결제)")
                    navigate("/mypage")
                }
                return
            }

            // 기존 포트원 선결제 로직 (신용카드, 카카오페이 등)
            const PortOne = (window as any).PortOne
            if (!PortOne) {
                alert("포트원 결제 모듈을 불러오지 못했습니다.")
                setSubmitting(false)
                return
            }

            sessionStorage.setItem("ORDER_TEMP_DATA", JSON.stringify(orderPayload))

            const INICIS_CHANNEL_KEY = "channel-key-33f17fde-5956-482b-a4c0-0c9eb7455398"
            // const KCP_CHANNEL_KEY = "channel-key-2f0e0993-9575-413a-abce-0872c4a10fcd"
            const KAKAO_CHANNEL_KEY = "YOUR_KAKAO_CHANNEL_KEY"

            // const selectedChannelKey = form.paymentMethod === "kakao" ? KAKAO_CHANNEL_KEY : KCP_CHANNEL_KEY
            const selectedChannelKey = form.paymentMethod === "kakao" ? KAKAO_CHANNEL_KEY : INICIS_CHANNEL_KEY
            const payMethod = form.paymentMethod === "kakao" ? "E_WALLET" : "CARD"

            const response = await PortOne.requestPayment({
                storeId: "store-a5787d08-083d-4f6d-97e3-52a9fd8d9ea4",
                channelKey: selectedChannelKey,
                paymentId: `mid_${new Date().getTime()}`,
                orderName: cartItems[0].productName + (cartItems.length > 1 ? ` 외 ${cartItems.length - 1}건` : ""),
                totalAmount: finalTotal,
                currency: "CURRENCY_KRW",
                payMethod: payMethod,
                customer: {
                    fullName: form.ordererName,
                    phoneNumber: form.ordererPhone,
                    email: form.ordererEmail,
                    address: {
                        addressLine1: form.address1,
                        addressLine2: form.address2,
                        zipcode: form.zipcode,
                    }
                },
                redirectUrl: `${window.location.origin}/payment/callback`,
            })

            if (response.code !== undefined) {
                alert(`결제에 실패하였습니다. (${response.message})`)
                setSubmitting(false)
                return
            }
            // 💡 결과를 res 변수에 담도록 수정
            const res = await createOrder({
                ...orderPayload,
                impUid: response.paymentId,
                merchantUid: response.paymentId
            })

            sessionStorage.removeItem("ORDER_TEMP_DATA")

            const finalOrderNo = (res && res.data) ? res.data : response.paymentId;

            // 💡 비회원은 알림창 대신 모달을 띄움
            if (isGuest) {
                setSuccessOrderNo(finalOrderNo);
            } else {
                alert("주문이 완료되었습니다.")
                navigate("/mypage")
            }

        } catch (error: any) {
            console.error("결제 프로세스 에러:", error)
            alert(`주문 처리 중 오류가 발생했습니다. (${error.message || error})`)
        } finally {
            setSubmitting(false)
        }
    }

    if (pageLoading) return <div className="p-20 text-center">주문 정보를 불러오는 중입니다...</div>

    const handleOpenPostcode = () => {
        setIsPostcodeOpen(!isPostcodeOpen)
    }

    return (
      <div className="min-h-screen bg-slate-50 text-slate-900">
          <Header />
          <main className="mx-auto max-w-7xl px-4 py-8 lg:px-6">
              <div className="mb-8">
                  <h1 className="text-3xl font-bold">주문 / 결제</h1>
                  {isGuest && <p className="mt-2 text-rose-500 text-sm font-medium">비회원 주문을 진행 중입니다.</p>}
              </div>

              <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
                  <section className="space-y-6">
                      {/* 주문 상품 리스트 */}
                      <Card className="rounded-[28px] border border-slate-200 p-6 shadow-sm">
                          <h2 className="text-xl font-bold mb-5">주문 상품</h2>
                          {cartItems.map(item => (
                            <div key={item.productId || item.cartItemId} className="flex gap-4 mb-4 items-center">
                                <img src={item.thumbnailUrl} className="w-20 h-20 rounded-xl object-cover border border-slate-100" />
                                <div>
                                    <p className="font-bold">{item.productName}</p>
                                    {item.optionName && !item.productName.includes(item.optionName) && (
                                      <p className="text-xs text-slate-500 mt-1">[옵션: {item.optionName}]</p>
                                    )}
                                    <p className="text-sm text-slate-500 mt-1">{item.quantity}개 / {formatPrice(item.price * item.quantity)}</p>
                                </div>
                            </div>
                          ))}
                      </Card>

                      {/* 주문자 정보 영역 */}
                      <Card className="rounded-[28px] border border-slate-200 p-6 shadow-sm">
                          <h2 className="text-xl font-bold mb-5">주문자 정보</h2>
                          <div className="grid gap-5 md:grid-cols-2">
                              <div className="space-y-2"><Label htmlFor="ordererName">주문자명</Label><Input id="ordererName" value={form.ordererName} onChange={handleChange} /></div>
                              <div className="space-y-2"><Label htmlFor="ordererPhone">연락처</Label><Input id="ordererPhone" value={form.ordererPhone} onChange={handleChange} /></div>
                              <div className="space-y-2 col-span-1 md:col-span-2">
                                  <Label htmlFor="ordererEmail">이메일 *</Label>
                                  {/* 💡 4. 이메일 Input 컴포넌트에 ref 연결 */}
                                  <Input
                                    id="ordererEmail"
                                    type="email"
                                    ref={emailInputRef}
                                    value={form.ordererEmail}
                                    onChange={handleChange}
                                    placeholder="결제 내역을 안내받으실 이메일을 입력해주세요."
                                  />
                              </div>
                              {isGuest && (
                                <div className="space-y-2 col-span-1 md:col-span-2 mt-2 bg-rose-50 p-4 rounded-2xl border border-rose-100">
                                    <Label htmlFor="orderPassword" className="text-rose-700 font-bold">주문 조회용 비밀번호 *</Label>
                                    <Input id="orderPassword" type="password" value={form.orderPassword} onChange={handleChange} placeholder="숫자/영문 조합 4자리 이상" className="bg-white border-rose-200" />
                                    <p className="text-xs text-rose-500 mt-1">※ 비회원 주문 조회 시 꼭 필요한 비밀번호입니다. 잊지 않게 주의해 주세요.</p>
                                </div>
                              )}
                          </div>
                      </Card>

                      {/* 배송지 정보 영역 */}
                      <Card className="rounded-[28px] border border-slate-200 p-6 shadow-sm">
                          <div className="flex items-center justify-between mb-5">
                              <h2 className="text-xl font-bold">배송지 정보</h2>
                              <div className="flex gap-3">
                                  <div className="flex items-center gap-2">
                                      <Checkbox id="same-as-orderer" checked={isSameAsOrderer} onCheckedChange={handleSameAsOrderer} />
                                      <Label htmlFor="same-as-orderer" className="text-sm cursor-pointer">주문자 정보와 동일</Label>
                                  </div>
                                  {!isGuest && (
                                    <Button variant="outline" size="sm" className="rounded-lg h-8 text-xs" onClick={() => setIsAddressModalOpen(true)}>배송지 목록</Button>
                                  )}
                              </div>
                          </div>

                          <div className="grid gap-5">
                              <div className="grid gap-5 md:grid-cols-2">
                                  <div className="space-y-2"><Label>받는 분</Label><Input id="receiverName" value={form.receiverName} onChange={handleChange} /></div>
                                  <div className="space-y-2"><Label>연락처</Label><Input id="receiverPhone" value={form.receiverPhone} onChange={handleChange} /></div>
                              </div>
                              <div className="grid gap-3 md:grid-cols-[160px_1fr]">
                                  <div className="space-y-2"><Label>우편번호</Label><Input id="zipcode" value={form.zipcode} onChange={handleChange} /></div>
                                  <div className="flex items-end">
                                      <Button type="button" variant="outline" className="rounded-xl" onClick={handleOpenPostcode}>
                                          주소 검색
                                      </Button>
                                  </div>
                              </div>
                              {isPostcodeOpen && (
                                <div className="border border-slate-200 rounded-2xl overflow-hidden relative shadow-inner">
                                    <div className="bg-slate-100 flex justify-between items-center p-2 border-b border-slate-200">
                                        <span className="text-sm font-bold text-slate-700 pl-3">우편번호 검색</span>
                                        <button type="button" onClick={() => setIsPostcodeOpen(false)} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-800 rounded-full hover:bg-slate-200 transition-colors">
                                            &times;
                                        </button>
                                    </div>
                                    <div id="daum-postcode-container" style={{ width: '100%', height: '400px' }}></div>
                                </div>
                              )}
                              <div className="space-y-2"><Label>기본 주소</Label><Input id="address1" value={form.address1} readOnly onChange={handleChange} /></div>
                              <div className="space-y-2"><Label>상세 주소</Label><Input id="address2" value={form.address2} onChange={handleChange} placeholder="상세 주소를 입력해주세요." /></div>
                              <div className="space-y-2"><Label>배송메모</Label><Input id="deliveryMemo" value={form.deliveryMemo} onChange={handleChange} placeholder="배송 요청사항을 입력해주세요." /></div>
                          </div>
                      </Card>

                      {/* 배송 및 행사 일정 영역 */}
                      <Card className="rounded-[28px] border border-slate-200 p-6 shadow-sm">
                          <h2 className="text-xl font-bold mb-5 text-slate-900">배송 및 행사 일정</h2>
                          <div className="bg-slate-50 border border-slate-200 p-5 rounded-2xl flex flex-wrap items-center gap-y-4 gap-x-3 text-sm">
                              <div className="flex items-center gap-2">
                                  <span className="font-bold text-slate-700 w-[60px]">배송날짜</span>
                                  {/*<Input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="w-36 bg-white h-9" />*/}
                                  <Input
                                    type="date"
                                    value={deliveryDate}
                                    min={minDate}
                                    max={maxDate}
                                    onChange={(e) => setDeliveryDate(e.target.value)}
                                    className="w-36 bg-white h-9"
                                  />
                                  <Input readOnly value={deliveryDay} className="w-20 bg-slate-100 text-center text-slate-500 cursor-not-allowed h-9" />
                              </div>

                              <select
                                className="h-9 rounded-md border border-slate-200 bg-white px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                value={deliveryType}
                                onChange={(e) => setDeliveryType(e.target.value as "immediate" | "specified")}
                              >
                                  <option value="immediate">즉시배송 (빠른 배송)</option>
                                  <option value="specified">시간지정 (행사에 맞춤)</option>
                              </select>

                              {deliveryType === "specified" && (
                                <div className="flex items-center gap-2 before:content-['/'] before:text-slate-300 before:mx-1">
                                    <span className="font-bold text-slate-700">배송시간</span>
                                    <select className="h-9 rounded-md border border-slate-200 bg-white px-2 focus:ring-indigo-500" value={deliveryHour} onChange={(e) => setDeliveryHour(e.target.value)}>
                                        {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(h => (<option key={h} value={h}>{h}</option>))}
                                    </select>
                                    <span>시</span>
                                    <select className="h-9 rounded-md border border-slate-200 bg-white px-2 focus:ring-indigo-500" value={deliveryMinute} onChange={(e) => setDeliveryMinute(e.target.value)}>
                                        {['00', '10', '20', '30', '40', '50'].map(m => (<option key={m} value={m}>{m}</option>))}
                                    </select>
                                    <span>분</span>
                                </div>
                              )}

                              <div className="flex items-center gap-2 before:content-['/'] before:text-slate-300 before:mx-1 w-full sm:w-auto mt-2 sm:mt-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-200">
                                  <span className="font-bold text-slate-700">행사시간</span>
                                  <Input value={eventTime} onChange={(e) => setEventTime(e.target.value)} placeholder="예: 오후 2시 예식" className="w-full sm:w-48 bg-white h-9" />
                              </div>
                          </div>
                      </Card>

                      {/* 메시지 (리본/카드) 영역 */}
                      <Card className="rounded-[28px] border border-slate-200 p-6 shadow-sm">
                          <h2 className="text-xl font-bold mb-5">메시지 (리본/카드)</h2>

                          <div className="bg-slate-50 border border-slate-200 p-6 rounded-2xl">
                              <RadioGroup value={messageType} onValueChange={(v) => setMessageType(v as any)} className="flex flex-wrap gap-5 mb-6">
                                  <div className="flex items-center gap-2"><RadioGroupItem value="none" id="msg-none" /><Label htmlFor="msg-none" className="cursor-pointer">선택 안함</Label></div>
                                  <div className="flex items-center gap-2"><RadioGroupItem value="ribbon" id="msg-ribbon" /><Label htmlFor="msg-ribbon" className="cursor-pointer">리본</Label></div>
                                  <div className="flex items-center gap-2"><RadioGroupItem value="card" id="msg-card" /><Label htmlFor="msg-card" className="cursor-pointer">카드</Label></div>
                                  <div className="flex items-center gap-2"><RadioGroupItem value="ribbon_card" id="msg-rc" /><Label htmlFor="msg-rc" className="cursor-pointer">리본 + 카드</Label></div>
                              </RadioGroup>

                              {["ribbon", "ribbon_card"].includes(messageType) && (
                                <div className="border-2 border-emerald-400 bg-white p-5 rounded-xl mb-4 relative">
                                    <div className="absolute -top-3 left-4 bg-white px-2 text-sm font-bold text-emerald-600">리본 문구 작성</div>

                                    <div className="space-y-5 mt-2">
                                        <div>
                                            <Label className="text-emerald-800 font-bold mb-2 block">경조사어 (리본 왼쪽)</Label>
                                            <Input value={ribbonPhrase} onChange={(e) => setRibbonPhrase(e.target.value)} placeholder="직접 입력하거나 아래 추천 문구를 선택하세요." className="mb-2" />
                                            <div className="flex flex-wrap gap-2">
                                                {QUICK_PHRASES.map((phrase, idx) => (
                                                  <button
                                                    key={idx} type="button" onClick={() => setRibbonPhrase(phrase)}
                                                    className="px-3 py-1.5 bg-slate-50 border border-emerald-100 rounded-md text-xs text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 transition-colors"
                                                  >
                                                      {phrase}
                                                  </button>
                                                ))}
                                            </div>
                                        </div>

                                        <Separator className="bg-emerald-50" />

                                        <div>
                                            <Label className="text-emerald-800 font-bold mb-2 block">보내는 분 (리본 오른쪽)</Label>
                                            <Input value={ribbonSender} onChange={(e) => setRibbonSender(e.target.value)} placeholder="예: (주)대한민국 대표이사 홍길동 / 동창회 일동" />
                                        </div>
                                    </div>
                                </div>
                              )}

                              {["card", "ribbon_card"].includes(messageType) && (
                                <div className="border-2 border-emerald-400 bg-white p-5 rounded-xl relative mt-4">
                                    <div className="absolute -top-3 left-4 bg-white px-2 text-sm font-bold text-emerald-600">카드 내용 작성</div>
                                    <textarea
                                      value={cardContent} onChange={(e) => setCardContent(e.target.value)}
                                      placeholder="카드에 인쇄될 내용을 마음껏 적어주세요."
                                      className="w-full mt-2 border border-slate-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none min-h-[100px]"
                                    />
                                </div>
                              )}
                          </div>
                      </Card>

                      {/* 결제 수단 영역 */}
                      <Card className="rounded-[28px] border border-slate-200 p-6 shadow-sm">
                          <h2 className="text-xl font-bold mb-5">결제 수단</h2>
                          <RadioGroup value={form.paymentMethod} onValueChange={(v) => setForm({...form, paymentMethod: v})} className="flex flex-wrap gap-4">
                              <div className="flex items-center gap-2">
                                  <RadioGroupItem value="card" id="card" />
                                  <Label htmlFor="card" className="cursor-pointer">신용/체크카드</Label>
                              </div>
                              {/*<div className="flex items-center gap-2">*/}
                              {/*    <RadioGroupItem value="kakao" id="kakao" />*/}
                              {/*    <Label htmlFor="kakao" className="cursor-pointer">카카오페이</Label>*/}
                              {/*</div>*/}
                              <div className="flex items-center gap-2">
                                  <RadioGroupItem value="post_pay" id="post_pay" />
                                  <Label htmlFor="post_pay" className="cursor-pointer font-bold text-indigo-700">무통장입금/현장결제 (후결제)</Label>
                              </div>
                          </RadioGroup>
                      </Card>
                  </section>

                  {/* 최종 결제 금액 및 버튼 영역 */}
                  <aside>
                      <Card className="rounded-[28px] border border-slate-200 p-6 sticky top-24 shadow-sm">
                          <h2 className="text-xl font-bold mb-6">결제 금액</h2>
                          <div className="space-y-3 mb-6">
                              <div className="flex justify-between text-sm"><span>상품금액</span><span>{formatPrice(productTotal)}</span></div>
                              <div className="flex justify-between text-sm"><span>배송비</span><span>{shippingFee === 0 ? "무료" : formatPrice(shippingFee)}</span></div>

                              {!isGuest && (
                                <div className="flex justify-between text-sm"><span>할인금액</span><span className="text-rose-500">- {formatPrice(discountAmount)}</span></div>
                              )}

                              <Separator />
                              <div className="flex justify-between font-bold text-xl"><span>최종 결제금액</span><span className="text-indigo-600">{formatPrice(finalTotal)}</span></div>
                          </div>

                          <Button
                            className="w-full h-14 rounded-2xl bg-slate-900 text-lg font-bold hover:bg-slate-800 disabled:bg-slate-400 text-white"
                            onClick={handleSubmitOrder}
                            disabled={submitting}
                          >
                              {submitting
                                ? "처리 중..."
                                : form.paymentMethod === "post_pay"
                                  ? "주문 접수하기 (후결제)"
                                  : "결제하기"
                              }
                          </Button>

                          {form.paymentMethod === "post_pay" && (
                            <p className="text-xs text-center text-slate-500 mt-4 leading-relaxed">
                                후결제를 선택하셨습니다.<br/>담당자 확인 후 결제 안내를 도와드립니다.
                            </p>
                          )}
                      </Card>
                  </aside>
              </div>
          </main>

          {/* 배송지 목록 팝업 모달 */}
          {isAddressModalOpen && !isGuest && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                <div className="w-full max-w-md rounded-[28px] bg-white shadow-xl overflow-hidden flex flex-col max-h-[80vh]">
                    <div className="flex items-center justify-between p-6 border-b border-slate-100">
                        <h2 className="text-xl font-bold">배송지 선택</h2>
                        <button className="text-slate-400 hover:text-slate-700 text-2xl font-light" onClick={() => setIsAddressModalOpen(false)}>&times;</button>
                    </div>
                    <div className="p-6 overflow-y-auto space-y-4">
                        {addresses.length === 0 ? (
                          <p className="py-10 text-center text-slate-500 bg-slate-50 rounded-2xl">등록된 배송지가 없습니다.<br/>마이페이지에서 추가해주세요.</p>
                        ) : (
                          addresses.map(addr => (
                            <div key={addr.addressId} className="rounded-2xl border border-slate-200 p-4 relative cursor-pointer hover:border-slate-900 transition-colors" onClick={() => handleSelectAddress(addr)}>
                                {addr.defaultYn === 'Y' && (<span className="absolute top-4 right-4 text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded">기본</span>)}
                                <p className="font-bold text-slate-900">{addr.receiverName}</p>
                                <p className="text-sm text-slate-600 mt-1">{addr.receiverPhone}</p>
                                <p className="text-sm text-slate-600 mt-1">[{addr.zipcode}] {addr.address1} {addr.address2}</p>
                            </div>
                          ))
                        )}
                    </div>
                </div>
            </div>
          )}

          {/* 비회원 주문 완료 팝업 모달 */}
          {successOrderNo && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
                <div className="w-full max-w-md rounded-[28px] bg-white p-8 shadow-2xl text-center animate-fade-in relative">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 mb-6">
                        <svg className="h-8 w-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                    </div>

                    <h2 className="text-2xl font-black text-slate-800 mb-3 tracking-tight">주문이 완료되었습니다!</h2>

                    <div className="bg-rose-50 border border-rose-100 text-rose-700 text-sm font-medium py-3 px-4 rounded-xl mb-6 break-keep leading-relaxed">
                        비회원 주문 조회를 위해 아래 <strong className="font-bold text-rose-700 underline underline-offset-4">주문번호</strong>를 반드시 메모하거나 복사해 주세요.
                    </div>

                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 mb-8 flex flex-col items-center justify-center gap-3">
                        <span className="text-sm text-slate-500 font-bold">주문번호</span>
                        <span className="text-[22px] font-black tracking-wider text-slate-900">{successOrderNo}</span>

                        <button
                          onClick={handleCopyOrderNo}
                          className="mt-3 flex items-center justify-center gap-1.5 w-full h-11 rounded-xl bg-white border border-slate-300 text-sm font-bold text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition-colors shadow-sm"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
                            </svg>
                            주문번호 복사하기
                        </button>
                    </div>

                    <div className="flex gap-3">
                        <button
                          onClick={() => { setSuccessOrderNo(null); navigate("/"); }}
                          className="flex-1 h-14 rounded-xl border border-slate-300 bg-white text-slate-700 font-bold hover:bg-slate-50 transition-colors"
                        >
                            홈으로 가기
                        </button>
                        <button
                          onClick={() => { setSuccessOrderNo(null); navigate("/guest/order"); }}
                          className="flex-1 h-14 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-colors shadow-sm"
                        >
                            주문 내역 확인
                        </button>
                    </div>
                </div>
            </div>
          )}
          <Footer />
      </div>
    )
}